import React from 'react'

import { overviewContent } from '../ProjectOverview/content'
import ProjectModuleCard from '../ProjectModuleCard'
import {
  Arrow,
  Comparison,
  ComparisonCard,
  ComparisonLabel,
  ComparisonList,
  CostDetail,
  CostLabel,
  CostRow,
  DataColumns,
  DataPanel,
  Example,
  ExampleCode,
  ExampleSource,
  Examples,
  FeedbackLoop,
  Hero,
  HeroDescription,
  HeroLogo,
  HeroSubtitle,
  HeroTitle,
  Icon,
  ModuleGrid,
  ModuleIcon,
  ModuleList,
  Operations,
  OperationsDescription,
  OperationsRows,
  OperationsValue,
  OverviewRoot,
  Phase,
  PhaseBadge,
  PhaseDescription,
  PhaseTitle,
  Pipeline,
  PipelineNote,
  PipelineStep,
  PipelineStepDescription,
  PipelineStepNumber,
  PipelineStepTitle,
  Section,
  SectionDescription,
  SectionTitle,
  StatusTimeline,
  TechnologyGrid,
  TechnologyItem,
  TechnologyName,
  TechnologyRole,
  ToolBadge,
} from './styled'

const icon = (type) => {
  if (type === 'search' || type === 'tavily') return <><circle cx="11" cy="11" r="7" /><path d="m20 20-4-4" /></>
  if (type === 'evaluate') return <><circle cx="12" cy="12" r="9" /><path d="m8.5 12 2.2 2.2 4.8-4.8" /></>
  if (type === 'retry' || type === 'gitlab') return <><path d="M20 7v5h-5" /><path d="M18.5 16a8 8 0 1 1 .5-9" /></>
  if (type === 'synthesise') return <><path d="M6 3h8l4 4v14H6z" /><path d="M14 3v5h5M9 13h6M9 17h6" /></>
  if (type === 'events') return <><rect x="3" y="5" width="18" height="16" rx="2" /><path d="M8 3v4M16 3v4M3 10h18" /></>
  if (type === 'news') return <><path d="M5 4h14v16H5z" /><path d="M8 8h8M8 12h8M8 16h5" /></>
  if (type === 'fastapi') return <path d="m13 2-9 12h8l-1 8 9-12h-8z" />
  if (type === 'sqlite') return <><ellipse cx="12" cy="5" rx="8" ry="3" /><path d="M4 5v14c0 1.7 3.6 3 8 3s8-1.3 8-3V5M4 12c0 1.7 3.6 3 8 3s8-1.3 8-3" /></>
  if (type === 'playwright') return <><rect x="3" y="4" width="18" height="14" rx="2" /><path d="M8 22h8M12 18v4" /></>
  if (type === 'openrouter') return <><path d="M4 8h10l-3-3M14 8l-3 3" /><path d="M20 16H10l3-3M10 16l3 3" /></>
  return <><rect x="5" y="4" width="14" height="17" rx="2" /><path d="M9 4V2h6v2M9 12l2 2 4-4" /></>
}

export default function CompetitiveIntelligenceOverview({ t }) {
  const content = overviewContent({ t })

  return (
    <OverviewRoot>
      <Hero>
        <HeroLogo src="/icons/logo.svg" alt={content.hero.eyebrow} />
        <HeroTitle>{content.hero.title}</HeroTitle>
        <HeroSubtitle>{content.hero.subtitle}</HeroSubtitle>
        <HeroDescription>{content.hero.description}</HeroDescription>
      </Hero>

      <Section>
        <SectionTitle>{content.comparison.title}</SectionTitle>
        <Comparison>
          <ComparisonCard before>
            <ComparisonLabel before>{content.comparison.beforeLabel}</ComparisonLabel>
            <ComparisonList before>{content.comparison.before.map(item => <li key={item}>{item}</li>)}</ComparisonList>
          </ComparisonCard>
          <Arrow aria-hidden="true">→</Arrow>
          <ComparisonCard>
            <ComparisonLabel>{content.comparison.afterLabel}</ComparisonLabel>
            <ComparisonList>{content.comparison.after.map(item => <li key={item}>{item}</li>)}</ComparisonList>
          </ComparisonCard>
        </Comparison>
      </Section>

      <Section alternate>
        <SectionTitle>{content.pipeline.title}</SectionTitle>
        <SectionDescription>{content.pipeline.subtitle}</SectionDescription>
        <Pipeline>
          {content.pipeline.steps.map((step, index) => (
            <React.Fragment key={step.key}>
              <PipelineStep>
                <PipelineStepNumber>{index + 1}</PipelineStepNumber>
                <Icon viewBox="0 0 24 24">{icon(step.key)}</Icon>
                <PipelineStepTitle>{step.title}</PipelineStepTitle>
                <PipelineStepDescription>{step.description}</PipelineStepDescription>
                <ToolBadge tool={step.tool}>{step.tool}</ToolBadge>
              </PipelineStep>
              {index < content.pipeline.steps.length - 1 ? <Arrow pipeline aria-hidden="true">→</Arrow> : null}
            </React.Fragment>
          ))}
        </Pipeline>
        <FeedbackLoop>↶ {content.pipeline.retryLabel}</FeedbackLoop>
        <PipelineNote>{content.pipeline.note}</PipelineNote>
      </Section>

      <Section>
        <SectionTitle>{content.intelligence.title}</SectionTitle>
        <SectionDescription>{content.intelligence.subtitle}</SectionDescription>
        <ModuleGrid>{content.intelligence.modules.map(module => <ProjectModuleCard key={module.key} module={module} renderIcon={icon} />)}</ModuleGrid>
      </Section>

      <Section alternate>
        <SectionTitle>{content.research.title}</SectionTitle>
        <SectionDescription>{content.research.subtitle}</SectionDescription>
        <ModuleGrid>{content.research.sources.map(module => <ProjectModuleCard key={module.key} module={module} renderIcon={icon} research />)}</ModuleGrid>
        <DataPanel>
          <h3>{content.research.panel.searchedTitle}</h3>
          <p>{content.research.panel.searchedDescription}</p>
          <DataColumns>
            <div><strong>{content.research.panel.competitorsTitle}</strong><p>{content.research.panel.competitors}</p></div>
            <div><strong>{content.research.panel.conditionsTitle}</strong><p>{content.research.panel.conditions}</p><small>{content.research.panel.conditionsNote}</small></div>
          </DataColumns>
          <h3>{content.research.panel.examplesTitle}</h3>
          <Examples>{content.research.panel.examples.map(([source, query]) => <Example key={source}><ExampleSource>{source}</ExampleSource><ExampleCode>{query}</ExampleCode></Example>)}</Examples>
          <p>{content.research.panel.queryNote}</p>
          <h3>{content.research.panel.scheduleTitle}</h3>
          <p>{content.research.panel.scheduleDescription}</p>
          <h3>{content.research.panel.expectationsTitle}</h3>
          <ModuleList>{content.research.panel.expectations.map(item => <li key={item}>{item}</li>)}</ModuleList>
        </DataPanel>
      </Section>

      <Section>
        <SectionTitle>{content.technology.title}</SectionTitle>
        <TechnologyGrid>{content.technology.items.map(([key, name, role]) => <TechnologyItem key={key}><ModuleIcon type={key}><Icon viewBox="0 0 24 24">{icon(key)}</Icon></ModuleIcon><TechnologyName>{name}</TechnologyName><TechnologyRole>{role}</TechnologyRole></TechnologyItem>)}</TechnologyGrid>
      </Section>

      <Section alternate>
        <SectionTitle>{content.operations.title}</SectionTitle>
        <Operations>
          <OperationsValue>{content.operations.value}</OperationsValue>
          <OperationsDescription>{content.operations.description}</OperationsDescription>
          <OperationsRows>{content.operations.items.map(([label, detail]) => <CostRow key={label}><CostLabel>{label}</CostLabel><CostDetail>{detail}</CostDetail></CostRow>)}</OperationsRows>
          <PipelineNote>{content.operations.note}</PipelineNote>
        </Operations>
      </Section>

      <Section>
        <SectionTitle>{content.status.title}</SectionTitle>
        <StatusTimeline>{content.status.phases.map((phase, index) => <Phase key={`${phase.title}-${index}`} status={phase.key}><PhaseBadge status={phase.key}>{phase.badge}</PhaseBadge><PhaseTitle>{phase.title}</PhaseTitle><PhaseDescription>{phase.description}</PhaseDescription></Phase>)}</StatusTimeline>
      </Section>
    </OverviewRoot>
  )
}
